/**
 * draftStorage.ts — the webapp's FIRST shared browser-storage wrapper (SB-8).
 *
 * Every prior localStorage site calls the API raw (gss.theme, gss.appMode,
 * auth:token, mc:tenantId); drafts are the first state where a storage
 * failure mid-flow would otherwise throw inside a render or an effect. So:
 * every access is try/caught — "storage unavailable" is NON-FATAL, the
 * App.tsx appMode precedent — and a failed read is indistinguishable from
 * "no draft", which is the correct degraded behaviour for accident
 * recovery.
 *
 * Keys are fully namespaced by the caller via draftKey():
 *   gss.draft.<form>.<tenantId>.<subject>
 * following the gss. prefix convention. Tenant + subject come from /v1/me —
 * the session truth — NEVER mc:tenantId (which defaults to the literal
 * "dev-tenant" and can silently disagree in WorkOS mode).
 */

export function draftKey(form: string, tenantId: string, subject: string): string {
  return `gss.draft.${form}.${tenantId}.${subject}`
}

export function readDraft<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? null : (JSON.parse(raw) as T)
  } catch {
    return null // storage unavailable or corrupt JSON — no draft, never a throw
  }
}

export function writeDraft(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* storage unavailable/full — non-fatal, the draft is best-effort */
  }
}

export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* non-fatal */
  }
}
